import { ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { AppLoader } from '@sternum/web/components/AppLoader';
import { throwAppError } from '@sternum/web/components/ErrorScreen';
import { useLogout, useMe, useSaveLastUsedClient } from '@sternum/web/features/auth/hooks';
import { useAutoUpdatedRef } from '@sternum/web/hooks';
import { namespaces } from '@sternum/web/services/i18n';

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { t } = useTranslation(namespaces.common);
  const { error, isLoading, isFetching, currentUser } = useMe();
  const { clientId } = useParams();
  const { mutateAsync: saveLastUsedClient } = useSaveLastUsedClient();
  const { logout } = useLogout();
  const isLogoutAlreadyTriggered = useRef(false);
  const saveLastUsedClientRef = useAutoUpdatedRef(saveLastUsedClient);

  const isUnauthorizedError = !isFetching && error?.data?.code === 'UNAUTHORIZED';
  const isForbiddenError = !isFetching && error?.data?.code === 'FORBIDDEN';

  useEffect(() => {
    if (!clientId) return;

    saveLastUsedClientRef.current?.({ clientId }).catch(console.error);
  }, [saveLastUsedClientRef, clientId]);

  useEffect(() => {
    if (!isUnauthorizedError) return;

    if (currentUser?.role === 'SANDBOX') {
      throwAppError({
        reason: 'not-authorized',
        title: t('errors.authorisationRequired.title'),
        description: t('errors.authorisationRequired.description'),
      });
    } else if (!isLogoutAlreadyTriggered.current) {
      logout();
      isLogoutAlreadyTriggered.current = true;
    }
  }, [logout, isUnauthorizedError, currentUser?.role, t]);

  if (isLoading) {
    return <AppLoader />;
  }

  if (error) {
    if (isUnauthorizedError) {
      return null;
    }

    if (isForbiddenError) {
      throwAppError({
        reason: 'forbidden',
        title: t('errors.forbidden.title'),
        description: t('errors.forbidden.noClientAccessDescription'),
      });
    }

    console.error(`[ProtectedRoute]: Failed to fetch current user: ${error}`);

    throwAppError({ reason: 'unknown' });
  }

  return <>{children}</>;
}
